import { API } from "@swivl-mono/api-helper";
import { ArrayHelper, ObjectHelper, warn } from "@swivl-mono/helpers";
import { AppType, AvailableMembersMap, MemberInterface, MemberInviteInterface, MemberInviteStatus, RequireOnly } from "@swivl-mono/types";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import AccountModel from "../Account/AccountModel";
import CallModel from "../CallModel/Call.Model";
import { LocalStorageHelper } from "../LocalStorage/LocalStorageHelper";
import { Member } from "./Member.Current";

interface StoreInterface {
  members?: MemberInterface[];
  membersMap?: { [memberId: number]: MemberInterface };
  invites?: MemberInviteInterface[];

  availableMembers: AvailableMembersMap;
  isAvailable: boolean;
}

const BlankState: StoreInterface = {
  members: undefined,
  invites: undefined,
  availableMembers: {},
  isAvailable: false
};

export default class MembersModel {
  static useState = create<StoreInterface>()(
    persist(() => ({ ...BlankState }), {
      name: "member-availability",
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        isAvailable: state.isAvailable
      })
    })
  );
  static setState = (state: Partial<StoreInterface>) => {
    MembersModel.useState.setState(state);
  };

  static interval = setInterval(async () => {
    await MembersModel.sendAvailabilityIfNeeded();
  }, 10000);

  static get state() {
    return MembersModel.useState.getState();
  }

  static reset() {
    MembersModel.setState({ ...BlankState });
  }

  static async memberLoggedIn() {
    if (!Member.id || !Member.orgId) return;

    // Sync the local `isAvailable` from the server's authoritative state.
    // Previously the local default was `false` and the 10-second heartbeat
    // would PUT false → Redis deletes the entry — kicking the agent offline
    // on every page load until they manually toggled Available again. The
    // server is the source of truth here; mirror it on login.
    try {
      const serverAvailable = await API.Members.getMemberAvailability(Member.orgId, Member.id);
      MembersModel.setState({ isAvailable: serverAvailable });
      if (serverAvailable) {
        // Bootstrap the Twilio Voice token so the agent can pick up calls
        // immediately after login — the manual Available toggle path does
        // this in `setAvailability(true)`; the silent-sync path needs the
        // same bootstrap.
        API.Apps.getAppByTypeForLocation(AppType.TWILIO, Member.orgId).then((twilioApp) => {
          if (twilioApp) CallModel.generateMemberTwilioToken(twilioApp, { force: true });
        });
      }
    } catch (err) {
      warn("Failed to sync availability on login", err);
    }
  }

  static sortMembers(members: MemberInterface[]): MemberInterface[] {
    return [...members].sort(
      (m1, m2) => m1.account.firstName.localeCompare(m2.account.firstName) || m1.account.lastName.localeCompare(m2.account.lastName)
    );
  }

  static sortInvites(invites: MemberInviteInterface[]): MemberInviteInterface[] {
    return [...invites].sort((i1, i2) => i1.firstName.localeCompare(i2.firstName) || i1.lastName.localeCompare(i2.lastName));
  }

  static async loadMembers(force = false) {
    if (this.state.members && !force) {
      return;
    }
    API.Members.loadMembers(Member.orgId!)
      .then((members) => {
        const membersMap = {};
        members.forEach((m) => {
          membersMap[m.id] = m;
        });

        MembersModel.setState({
          membersMap,
          members: MembersModel.sortMembers(members)
        });
      })
      .catch((error) => {
        warn("Error loading members", error);
      });
  }

  static async updateMember(memberToUpdate: RequireOnly<MemberInterface, "id">, supressSave?: boolean) {
    let membersMap = MembersModel.state.membersMap ? { ...MembersModel.state.membersMap } : {};
    const isMe = memberToUpdate.id === Member.id;

    let member = isMe ? Member.member : membersMap[memberToUpdate.id];
    if (!member) {
      warn("Member not found in state", membersMap, memberToUpdate, memberToUpdate.id, membersMap[memberToUpdate.id]);
      return;
    }

    member = Object.assign({}, member, memberToUpdate);

    if (!supressSave) {
      if (isMe) {
        this.saveAccountMember(member);
      }
    }

    membersMap[member.id] = member;
    MembersModel.setState({ members: MembersModel.sortMembers(Object.values(membersMap)), membersMap });
    if (supressSave) {
      return;
    }
    let newMember: Partial<MemberInterface> = { ...member };
    delete newMember.createdAt;
    delete newMember.updatedAt;
    delete newMember.org;
    delete newMember.account;
    delete (newMember as any).jwt;

    try {
      const updateMember = isMe
        ? await API.Members.updateMyMember(Member.orgId!, member.id, ObjectHelper.stripNulls(newMember as any))
        : await API.Members.updateMember(Member.orgId!, member.id, ObjectHelper.stripNulls(newMember as any));

      membersMap[updateMember.id] = updateMember;

      // if (isMe) {
      //   this.saveAccountMember(member);
      // }
      membersMap[member.id] = updateMember;
      MembersModel.setState({ members: MembersModel.sortMembers(Object.values(membersMap)), membersMap });
    } catch (error) {
      warn("Error updating member", error);
    }
  }

  static saveAccountMember(memberToUpdate: RequireOnly<MemberInterface, "id">) {
    let accountMember = AccountModel.state.member;
    if (accountMember && accountMember.id === memberToUpdate.id) {
      accountMember = Object.assign({}, accountMember, memberToUpdate);
      AccountModel.setState({ member: accountMember });
      LocalStorageHelper.setItem("member", JSON.stringify(accountMember));
    }
  }

  static async addNewRoleToMember(memberId: number) {
    return API.Members.addRoleToMember(Member.orgId!, memberId)
      .then((member) => {
        this.updateMember(member, true);
      })
      .catch((error) => {
        warn("Error adding role to member", error);
      });
  }

  static async loadInvites() {
    return API.MemberInvite.loadInvites(Member.orgId!)
      .then((invites) => {
        MembersModel.setState({ invites: MembersModel.sortInvites(invites) });
      })
      .catch((error) => {
        warn("Error loading invites", error);
      });
  }

  static updateInvite(invite: MemberInviteInterface) {
    let invites = MembersModel.state.invites ? [...MembersModel.state.invites] : [];
    let index = invites.findIndex((m) => m.id === invite.id);

    if (index >= 0) {
      invites[index] = invite;
    } else {
      invites.push(invite);
    }

    MembersModel.setState({ invites: MembersModel.sortInvites(invites) });

    const strippedInvited: Partial<MemberInviteInterface> = {
      id: invite.id,
      firstName: invite.firstName.trim(),
      lastName: invite.lastName.trim(),
      email: invite.email.trim(),
      status: invite.status,
      permissions: invite.permissions
    };
    const body: Partial<MemberInviteInterface> = ObjectHelper.stripNulls(strippedInvited as any);
    // Added after stripNulls, so choosing "No persona" sends null and clears it (ENG-3433). Only an invite
    // that came back with the field sends it, so a server from before the field, which rejects unknown
    // properties, never sees it.
    if ("persona" in invite) body.persona = invite.persona ?? null;

    return API.MemberInvite.updateInvite(Member.orgId!, invite.id, body)
      .then((invite) => {
        let invites = MembersModel.state.invites ? [...MembersModel.state.invites] : [];
        let index = invites.findIndex((m) => m.id === invite.id);
        if (index >= 0) {
          invites[index] = invite;
        } else {
          invites.push(invite);
        }

        MembersModel.setState({ invites: MembersModel.sortInvites(invites) });
      })
      .catch((error) => {
        warn("Error updating member", error);
      });
  }

  static createInvite(invite: Partial<MemberInviteInterface>) {
    invite.firstName = invite.firstName?.trim();
    invite.lastName = invite.lastName?.trim();
    invite.email = invite.email?.trim();
    return API.MemberInvite.createInvite(Member.orgId!, invite)
      .then((invite) => {
        let invites = MembersModel.state.invites ? MembersModel.state.invites : [];
        invites.push(invite);
        MembersModel.setState({ invites: MembersModel.sortInvites(invites) });
        return invite;
      })
      .catch((error) => {
        warn("Error updating member", error);
        throw error;
      });
  }

  static deleteInvite(invite: Partial<MemberInviteInterface>) {
    return API.MemberInvite.denyOrRevokeInvite(Member.orgId!, invite.uuid!, MemberInviteStatus.REVOKED)
      .then((deleted: MemberInviteInterface) => {
        if (deleted.status === MemberInviteStatus.REVOKED) {
          let invites = MembersModel.state.invites ? MembersModel.state.invites : [];
          let index = invites.findIndex((m) => m.id === invite.id);
          invites.splice(index, 1);
          MembersModel.setState({ invites: invites });
        } else {
          warn("Error deleting invite", deleted);
        }
        return deleted;
      })
      .catch((error) => {
        warn("Error updating member", error);
        throw error;
      });
  }

  static resendInvite(invite: Partial<MemberInviteInterface>) {
    return API.MemberInvite.resendInvite(Member.orgId!, invite.uuid!).catch((error) => {
      warn("Error resendInvite for member", error);
      throw error;
    });
  }

  static async deleteMember(memberId: number) {
    return API.Members.deleteMember(Member.orgId!, memberId)
      .then((_) => {
        const state = { ...MembersModel.state };
        let members = state.members;
        let membersMap = state.membersMap;
        if (!members) {
          return;
        }
        if (membersMap) {
          delete membersMap[memberId];
        }
        let index = members.findIndex((m) => m.id === memberId);
        members.splice(index, 1);
        MembersModel.setState({ members, membersMap });
      })
      .catch((error) => {
        warn("Error deleting member", error);
        throw error;
      });
  }

  static sendAvailabilityIfNeeded() {
    // Only re-assert "available" on the heartbeat — the server uses a 120s
    // TTL on the Redis presence key, so silence eventually evicts the
    // agent. Actively PUTting false on every heartbeat (when local state is
    // false) made cross-tab and external availability writes get clobbered
    // every 10s. Explicit setAvailability(false) from the UI still PUTs
    // false directly for immediate-offline semantics.
    if (Member.id && MembersModel.state.isAvailable) {
      API.Members.setMemberAvailability(Member.orgId, Member.id, true);
    }
  }
  static async setAvailability(available: boolean) {
    console.log(`🔄 Member ${Member.id} toggling availability to ${available} (orgId: ${Member.orgId})`);
    MembersModel.setState({ isAvailable: available });
    if (Member.id) {
      await API.Members.setMemberAvailability(Member.orgId, Member.id, MembersModel.state.isAvailable);
      console.log(`✅ API call complete for member ${Member.id}, refetching...`);
      // Immediately refetch to update the nav bar count
      await MembersModel.getMembersAvailability();
      console.log(`✅ Refetch complete for member ${Member.id}`);
    }
    if (available) {
      API.Apps.getAppByTypeForLocation(AppType.TWILIO, Member.orgId!).then((twilioApp) => {
        if (!twilioApp) {
          warn("No Twilio app found, cannot generate new token");
          return;
        }
        CallModel.generateMemberTwilioToken(twilioApp, { force: true });
      });
    } else {
      CallModel.clearTwilioToken();
    }
  }

  static async subscribeToPushNotifications() {
    const perm = await Notification.requestPermission();

    if (perm !== "granted") throw new Error("Notifications not granted");

    const reg = await navigator.serviceWorker.register("/service-worker.js", { scope: "/" });

    if (!reg.active) {
      const sw = reg.installing || reg.waiting;
      if (sw) {
        await Promise.race([
          new Promise<void>((resolve) => {
            sw.addEventListener("statechange", () => {
              if (sw.state === "activated") resolve();
            });
          }),
          new Promise<void>((_, reject) => setTimeout(() => reject(new Error("Service worker activation timed out")), 15000))
        ]);
      }
    }

    const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: ArrayHelper.urlBase64ToUint8Array(vapidPublicKey)
    });
    await API.Push.subscribe(Member.orgId!, Member.id!, sub);
  }
  static async unsubscribeFromPushNotifications() {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();

      for (const registration of registrations) {
        await registration.unregister();
      }

      // 4) Delete subscription from your server
      await API.Push.unsubscribeFromAll(Member.orgId!, Member.id!);
    } catch (e) {}
  }

  static memberAvailabilityUpdated(availableMembers: AvailableMembersMap) {
    MembersModel.setState({ availableMembers: availableMembers });
  }
  static getMembersAvailability() {
    return API.Members.getAvailableMembers(Member.orgId!)
      .then((availableMembers) => {
        MembersModel.setState({ availableMembers: availableMembers });
      })
      .catch((error) => {
        warn("Error getting members availability", error);
      });
  }
}
